import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ClipboardCheck, CheckCircle2, X, Clock, Video, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRefs } from "@/lib/useRefs";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState, TypeBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Approvals() {
  const { user } = useAuth();
  const refs = useRefs();
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [comment, setComment] = useState("");
  const [progFilter, setProgFilter] = useState("all");
  const [awaiting, setAwaiting] = useState([]);

  const canApprove = user?.is_admin || user?.role_category === "rc_approver";

  const load = () => {
    api.get("/documents/pending").then((r) => setItems(r.data)).catch(() => {});
    api.get("/documents/awaiting").then((r) => setAwaiting(r.data)).catch(() => {});
  };
  useEffect(() => { if (canApprove) load(); }, [canApprove]);

  const notify = () => window.dispatchEvent(new Event("approvals-changed"));

  const approve = async (id) => {
    try { await api.post(`/documents/${id}/approve`); toast.success("Approved"); load(); notify(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    try { await api.post(`/documents/${rejectFor.id}/reject`, { comment }); toast.success("Sent back to draft"); setRejectFor(null); setComment(""); load(); notify(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!canApprove) return <Navigate to="/app/dashboard" replace />;

  const programsWithItems = [...new Set(items.map((i) => i.program_id))];
  const filtered = progFilter === "all" ? items : items.filter((i) => i.program_id === progFilter);

  return (
    <div>
      <PageHeader
        eyebrow="Console · Approvals"
        title="Approvals"
        subtitle="One queue across every program you own. Content goes live to users and the AI assistant only after sign-off."
        testid="approvals-header">
        {programsWithItems.length > 1 && (
          <Select value={progFilter} onValueChange={setProgFilter}>
            <SelectTrigger className="w-56 rounded-full bg-card" data-testid="approvals-program-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs ({items.length})</SelectItem>
              {programsWithItems.map((pid) => (
                <SelectItem key={pid} value={pid}>
                  {refs.programName(pid)} ({items.filter((i) => i.program_id === pid).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      {filtered.length === 0 ? (
        <EmptyState icon={ClipboardCheck} title="Nothing awaiting your approval" hint="Submitted content that needs you will appear here." />
      ) : (
        <div className="space-y-3 stagger" data-testid="approvals-list">
          {filtered.map((d) => (
            <div key={d.id} className="kp-card kp-card-lift p-5" data-testid={`approval-${d.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <TypeBadge type={d.type} />
                <div className="min-w-0 flex-1">
                  <button onClick={() => setPreview(d)} className="font-display text-lg text-foreground hover:text-primary text-left leading-tight transition-colors">
                    {d.title}
                  </button>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-3 flex-wrap mt-1.5">
                    <span className="rounded-full bg-secondary/60 text-primary px-2 py-0.5">{refs.programName(d.program_id)}</span>
                    <span>{d.source}</span>
                    {d.videos?.length > 0 && <span className="flex items-center gap-1"><Video className="w-3 h-3" /> {d.videos.length}</span>}
                  </p>
                </div>
                <Badge variant="outline" className="bg-[#fdf2c9] text-[#7a5a12] border-[#f0d98c] font-medium">
                  {d.stage === "admin" ? "Awaiting Admin" : "Awaiting PM"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CheckCircle2 className={`w-3.5 h-3.5 ${d.pm_approved ? "text-[#25604d]" : "text-muted-foreground/40"}`} /> PM
                  <CheckCircle2 className={`w-3.5 h-3.5 ml-2 ${d.admin_approved ? "text-[#25604d]" : "text-muted-foreground/40"}`} /> Admin
                </span>
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 rounded-full" onClick={() => setRejectFor(d)} data-testid={`reject-${d.id}`}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button size="sm" className="rounded-full" onClick={() => approve(d.id)} data-testid={`approve-${d.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {awaiting.length > 0 && (
        <div className="mt-12" data-testid="awaiting-others-section">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-1">Oversight</p>
          <h3 className="font-display text-xl text-foreground">Waiting on someone else</h3>
          <p className="text-sm text-muted-foreground mb-4">Submitted content in your scope that another approver has to action.</p>
          <div className="kp-card divide-y divide-border overflow-hidden">
            {awaiting.map((d) => (
              <div key={d.id} className="p-4 flex items-center gap-3 flex-wrap hover:bg-secondary/20 transition" data-testid={`awaiting-${d.id}`}>
                <TypeBadge type={d.type} />
                <button onClick={() => setPreview(d)} className="text-sm font-semibold text-foreground hover:text-primary text-left min-w-0 flex-1 truncate transition-colors">{d.title}</button>
                <span className="text-[10px] font-mono uppercase tracking-wider rounded-full bg-secondary/50 text-primary px-2 py-0.5">{refs.programName(d.program_id)}</span>
                <Badge variant="outline" className="text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {d.waiting_on}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* preview */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {preview?.title}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{preview?.content}</div>
          {preview?.videos?.map((v, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Video className="w-4 h-4 text-primary" /> {v.title}</p>
              <p className="text-xs text-slate-500 mt-1">{v.transcript}</p>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      {/* reject */}
      <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject "{rejectFor?.title}"</DialogTitle></DialogHeader>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reason for rejection (sent back to draft)" rows={3} data-testid="reject-comment" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="bg-destructive hover:bg-destructive/90 rounded-full" onClick={doReject} data-testid="confirm-reject">Reject & return to draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
